/**
 * Account synchronization hook.
 * 
 * Orchestrates the industry-standard pattern:
 * 1. Initial HTTP load to populate the store
 * 2. WebSocket subscriptions for real-time updates
 * 
 * This hook should be called once at the app level when a user is authenticated.
 */

import { useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AccountsApi, PositionsApi, OrdersApi } from "../api-client";
import { useAccountStore, type WsPositionData } from "../store";
import { wsClient } from "../ws-client";
import { useUnifiedAuth } from "../use-unified-auth";
import type { WsOrderMessage, WsBalanceMessage, WsPositionMessage, WsFillMessage, WsFundingMessage } from "../ws-client";

// Throttle interval for position updates (250ms = fast UI updates with event-driven trigger system)
// Reduced from 1000ms since backend now has event-driven execution (sub-100ms latency)
const POSITION_UPDATE_THROTTLE_MS = 250;

/**
 * Main synchronization hook.
 * Handles initial HTTP load and WebSocket subscriptions.
 */
export function useAccountSync(userId: string | undefined) {
  const queryClient = useQueryClient();
  
  // Get store actions using getState() - these are stable references
  const storeActions = useMemo(() => ({
    setLoading: useAccountStore.getState().setLoading,
    syncFromHttp: useAccountStore.getState().syncFromHttp,
    applyWsPositionUpdate: useAccountStore.getState().applyWsPositionUpdate,
    applyWsOrderUpdate: useAccountStore.getState().applyWsOrderUpdate,
    applyWsBalanceUpdate: useAccountStore.getState().applyWsBalanceUpdate,
    applyWsFillUpdate: useAccountStore.getState().applyWsFillUpdate,
    applyWsFundingUpdate: useAccountStore.getState().applyWsFundingUpdate,
    reset: useAccountStore.getState().reset,
  }), []);
  
  // Subscribe to store state we need to return
  const isInitialized = useAccountStore((state) => state.isInitialized);
  
  // Use unified auth to check if user has valid authentication (wallet token OR email session)
  const { hasValidAuth } = useUnifiedAuth();
  const canSync = !!userId && hasValidAuth;

  // Initial HTTP load - fetches all account data
  // Also ensures account exists (creates if needed) so orders can be placed immediately
  const { isLoading, error, refetch } = useQuery({
    queryKey: ["account-sync", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      storeActions.setLoading(true);
      
      try {
        // First, ensure account exists (create if needed)
        // This runs once on wallet connect, so orders don't need to check
        let accountResponse = await AccountsApi.getAccount().catch(() => null);
        
        if (!accountResponse) {
          // Account doesn't exist -- create it (server defaults to zero collateral).
          // User will deposit funds later. Balances are server-authoritative.
          try {
            await AccountsApi.createAccount();
            // Fetch the newly created account
            accountResponse = await AccountsApi.getAccount().catch(() => null);
          } catch {
            // Account creation failed - might already exist (race condition), try fetching again
            accountResponse = await AccountsApi.getAccount().catch(() => null);
          }
        }

        // Fetch positions and orders in parallel
        const [positions, orders] = await Promise.all([
          PositionsApi.listPositions().catch(() => []),
          OrdersApi.listOrders().catch(() => []),
        ]);

        // Sync to store
        storeActions.syncFromHttp({
          account: accountResponse || undefined,
          positions,
          orders,
        });

        return { success: true };
      } catch (err) {
        storeActions.setLoading(false);
        throw err;
      }
    },
    enabled: canSync,
    staleTime: Infinity, // Don't refetch automatically - WebSocket handles updates
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });

  // Refs for throttled position updates
  const pendingPositionUpdates = useRef<Map<string, WsPositionData>>(new Map());
  const positionThrottleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Refs for throttled balance updates
  const pendingBalanceUpdate = useRef<WsBalanceMessage | null>(null);
  const balanceThrottleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush pending position updates to store
  const flushPositionUpdates = useCallback(() => {
    const updates = pendingPositionUpdates.current;
    if (updates.size > 0) {
      // Apply all pending updates
      updates.forEach((update) => {
        storeActions.applyWsPositionUpdate(update);
      });
      updates.clear();
    }
    positionThrottleTimer.current = null;
  }, [storeActions]);

  // Flush pending balance update to store
  const flushBalanceUpdate = useCallback(() => {
    const update = pendingBalanceUpdate.current;
    if (update) {
      storeActions.applyWsBalanceUpdate({
        user_id: update.user_id,
        collateral: update.collateral,
        available_margin: update.available_margin,
        used_margin: update.used_margin,
        unrealized_pnl: update.unrealized_pnl,
        timestamp: update.timestamp,
      });
      pendingBalanceUpdate.current = null;
    }
    balanceThrottleTimer.current = null;
  }, [storeActions]);

  // WebSocket subscriptions - update store with real-time data
  // Position and balance updates are THROTTLED to prevent UI flickering
  // Order updates are NOT throttled (important for UX)
  useEffect(() => {
    if (!canSync) return;

    // Subscribe to positions channel - THROTTLED
    // Position updates can be very frequent (P&L changes with price movements)
    const unsubPositions = wsClient.subscribe<WsPositionMessage>(
      "user/positions",
      (message) => {
        if (message.type === "position") {
          // Track if this position just got closed (TP/SL triggered or manual close)
          // Industry standard: verify critical state changes via HTTP for certainty
          const existingPosition = useAccountStore.getState().positions.get(message.market);
          const wasOpen = existingPosition && existingPosition.size > 0;
          const nowClosed = parseFloat(message.size) === 0;
          
          // Store in pending map (latest update per position ID)
          pendingPositionUpdates.current.set(message.id, {
            id: message.id,
            user_id: message.user_id,
            market: message.market,
            side: message.side,
            size: message.size,
            entry_price: message.entry_price,
            mark_price: message.mark_price,
            leverage: message.leverage,
            margin: message.margin,
            unrealized_pnl: message.unrealized_pnl,
            realized_pnl: message.realized_pnl,
            liquidation_price: message.liquidation_price,
            opened_at: message.opened_at,
            take_profit: message.take_profit,
            stop_loss: message.stop_loss,
            timestamp: message.timestamp,
          });
          
          // Schedule flush if not already scheduled
          if (!positionThrottleTimer.current) {
            positionThrottleTimer.current = setTimeout(flushPositionUpdates, POSITION_UPDATE_THROTTLE_MS);
          }
          
          // If position just got closed (TP/SL or manual), immediately refresh via HTTP
          // This ensures chart lines are removed even if WebSocket message was delayed/missed
          // Industry standard pattern: WebSocket for speed, HTTP for certainty on critical state changes
          if (wasOpen && nowClosed && userId) {
            setTimeout(async () => {
              try {
                const positions = await PositionsApi.listPositions();
                storeActions.syncFromHttp({ positions });
              } catch (error) {
                console.warn("[useAccountSync] Failed to refresh after position close:", error);
              }
            }, 300); // 300ms allows backend to commit to database
          }
        }
      }
    );

    // Subscribe to orders channel - NOT THROTTLED
    // Orders need immediate updates for good UX (fills, cancellations, etc.)
    const unsubOrders = wsClient.subscribe<WsOrderMessage>(
      "user/orders",
      (message) => {
        if (message.type === "order") {
          // Track if this order just got filled (industry standard: verify critical state changes via HTTP)
          const existingOrder = useAccountStore.getState().orders.get(message.order_id);
          const wasOpen = existingOrder && (
            existingOrder.status === "open" || 
            existingOrder.status === "pending" ||
            existingOrder.status === "partially_filled"
          );
          const nowFilled = message.status === "filled";
          
          // Apply the order update immediately (remove from open orders)
          storeActions.applyWsOrderUpdate({
            order_id: message.order_id,
            user_id: message.user_id,
            market: message.market,
            side: message.side,
            order_type: message.order_type,
            price: message.price,
            size: message.size,
            filled_size: message.filled_size,
            status: message.status,
            timestamp: message.timestamp,
          });
          
          // If order just got filled, immediately refresh positions AND orders via HTTP
          // This ensures:
          // 1. The new/updated position appears even if WS position update fails/delayed
          // 2. The filled order is removed from open orders (removes chart line) even if WS was missed
          // Industry standard pattern: WebSocket for speed, HTTP for certainty on critical state changes
          if (wasOpen && nowFilled && userId) {
            setTimeout(async () => {
              try {
                const [positions, orders] = await Promise.all([
                  PositionsApi.listPositions(),
                  OrdersApi.listOrders(),
                ]);
                storeActions.syncFromHttp({ positions, orders });
              } catch (error) {
                console.warn("[useAccountSync] Failed to refresh after order fill:", error);
              }
            }, 300); // 300ms allows backend to commit to database
          }
        }
      }
    );

    // Subscribe to balance channel - THROTTLED
    // Balance updates (from position P&L changes) can be frequent
    const unsubBalance = wsClient.subscribe<WsBalanceMessage>(
      "user/balances",
      (message) => {
        if (message.type === "balance") {
          // Store the latest balance update
          pendingBalanceUpdate.current = message;
          
          // Schedule flush if not already scheduled
          if (!balanceThrottleTimer.current) {
            balanceThrottleTimer.current = setTimeout(flushBalanceUpdate, POSITION_UPDATE_THROTTLE_MS);
          }
        }
      }
    );

    // Subscribe to fills channel - NOT THROTTLED
    // Fills are infrequent and important for trade history
    const unsubFills = wsClient.subscribe<WsFillMessage>(
      "user/fills",
      (message) => {
        if (message.type === "fill") {
          storeActions.applyWsFillUpdate({
            id: message.id,
            user_id: message.user_id,
            order_id: message.order_id,
            market: message.market,
            side: message.side,
            size: message.size,
            price: message.price,
            fee: message.fee,
            realized_pnl: message.realized_pnl,
            fill_type: message.fill_type,
            timestamp: message.timestamp,
          });
          
          // Invalidate trade history cache when new fill arrives
          // This triggers Performance Lab to refetch and show updated stats
          // Industry standard: WebSocket events trigger React Query cache invalidation
          queryClient.invalidateQueries({ queryKey: ["trade-history", userId] });
        }
      }
    );

    // Subscribe to funding channel - NOT THROTTLED
    // Funding payments are infrequent (once per funding period)
    const unsubFunding = wsClient.subscribe<WsFundingMessage>(
      "user/funding",
      (message) => {
        if (message.type === "funding_payment") {
          storeActions.applyWsFundingUpdate({
            id: message.id,
            user_id: message.user_id,
            market: message.market,
            position_size: message.position_size,
            funding_rate: message.funding_rate,
            payment: message.payment,
            timestamp: message.timestamp,
          });
        }
      }
    );

    return () => {
      unsubPositions();
      unsubOrders();
      unsubBalance();
      unsubFills();
      unsubFunding();
      
      // Clear any pending timers on cleanup
      if (positionThrottleTimer.current) {
        clearTimeout(positionThrottleTimer.current);
        positionThrottleTimer.current = null;
      }
      if (balanceThrottleTimer.current) {
        clearTimeout(balanceThrottleTimer.current);
        balanceThrottleTimer.current = null;
      }
    };
  }, [canSync, userId, queryClient, storeActions, flushPositionUpdates, flushBalanceUpdate]);

  // Reset store when user changes or logs out
  useEffect(() => {
    if (!userId) {
      storeActions.reset();
    }
  }, [userId, storeActions]);

  // Force refresh function - refetches all data via HTTP
  const forceRefresh = async () => {
    await refetch();
    // Also invalidate any React Query caches that might be stale
    queryClient.invalidateQueries({ queryKey: ["backend-account", userId] });
    queryClient.invalidateQueries({ queryKey: ["backend-positions", userId] });
    queryClient.invalidateQueries({ queryKey: ["backend-orders", userId] });
  };

  return {
    isLoading,
    isInitialized,
    error: error as Error | null,
    forceRefresh,
  };
}

/**
 * Hook to get positions from store.
 * Use this instead of useBackendPositions for reading data.
 */
export function useStorePositions() {
  // Select raw data from store
  const positionsMap = useAccountStore((state) => state.positions);
  const isInitialized = useAccountStore((state) => state.isInitialized);
  const isLoading = useAccountStore((state) => state.isLoading);
  
  // Derive the filtered positions list
  const positions = useMemo(() => {
    return Array.from(positionsMap.values())
      .filter(p => p.status === "open" && p.size > 0);
  }, [positionsMap]);
  
  return {
    positions,
    isLoading: isLoading || !isInitialized,
  };
}

/**
 * Hook to get open orders from store.
 * Use this instead of useBackendOrders for reading data.
 */
export function useStoreOrders() {
  // Select raw data from store
  const ordersMap = useAccountStore((state) => state.orders);
  const isInitialized = useAccountStore((state) => state.isInitialized);
  const isLoading = useAccountStore((state) => state.isLoading);
  
  // Derive the filtered orders list
  const orders = useMemo(() => {
    return Array.from(ordersMap.values())
      .filter(o => o.status === "open" || o.status === "pending" || o.status === "partially_filled");
  }, [ordersMap]);
  
  return {
    orders,
    isLoading: isLoading || !isInitialized,
  };
}

/**
 * Hook to get balance from store.
 * Use this instead of useBackendAccount for reading balance data.
 */
export function useStoreBalance() {
  // Select raw data from store
  const balance = useAccountStore((state) => state.balance);
  const isInitialized = useAccountStore((state) => state.isInitialized);
  const isLoading = useAccountStore((state) => state.isLoading);
  
  return {
    balance,
    isLoading: isLoading || !isInitialized,
  };
}
